import { Employee } from "../../employees/hooks/useEmployees";
import { DEFAULT_PR_CONFIG, PerformanceReviewConfig } from "./usePerformanceReviewConfig";

export type PerformanceReviewScenarioType =
  | "NO_REVIEW_12M"
  | "NEW_MANAGER_NO_REVIEW"
  | "LONG_TENURE_NO_REVIEW"
  | "ROLE_CHANGE_REQUESTED"
  | "FIRST_REVIEW_UPCOMING";

export type PerformanceReviewSeverity = "medium" | "high";

export type PerformanceReviewScenario = {
  type: PerformanceReviewScenarioType;
  severity: PerformanceReviewSeverity;
  message: string;
  metricValue: number;
  unit: string;
};

export type PerformanceReviewMetrics = {
  daysSinceLastReview: number | null; // null = never had a review
  daysSinceStart: number;
  managerChangedDaysAgo: number | null; // null = no recent manager change
  reviewAfterManagerChange: boolean;
  wantsRoleChange: boolean;
  totalReviewsHeld: number;
};

export type PerformanceReviewEmployeeRow = {
  employee: Employee;
  scenarios: PerformanceReviewScenario[];
  metrics: PerformanceReviewMetrics;
  topSeverity: PerformanceReviewSeverity;
};

const signalPriority: Record<PerformanceReviewScenarioType, number> = {
  NEW_MANAGER_NO_REVIEW: 1,
  ROLE_CHANGE_REQUESTED: 2,
  LONG_TENURE_NO_REVIEW: 3,
  NO_REVIEW_12M: 4,
  FIRST_REVIEW_UPCOMING: 5
};

const severityRank: Record<PerformanceReviewSeverity, number> = {
  medium: 1,
  high: 2
};

const hashString = (value: string): number => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return hash >>> 0;
};

const mulberry32 = (seed: number) => {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const randomInt = (random: () => number, min: number, max: number) => {
  return Math.floor(random() * (max - min + 1)) + min;
};

export const getSimulationDayKey = (date = new Date()) => date.toISOString().slice(0, 10);

export const simulatePerformanceReviewMetrics = (
  employeeId: string,
  dayKey: string = getSimulationDayKey()
): PerformanceReviewMetrics => {
  const random = mulberry32(hashString(`perf:${employeeId}:${dayKey}`));

  const daysSinceStart = randomInt(random, 30, 1500);

  // Employees in first 90 days may not have had a review yet
  const totalReviewsHeld = daysSinceStart < 90 ? 0 : randomInt(random, 0, 5);

  const daysSinceLastReview = totalReviewsHeld === 0 ? null : randomInt(random, 0, 730);

  // ~30% of employees had a manager change in the last year
  const hadManagerChange = random() > 0.7;
  const managerChangedDaysAgo = hadManagerChange ? randomInt(random, 30, 365) : null;

  // If there was a manager change, was there a review afterward?
  const reviewAfterManagerChange = hadManagerChange ? random() > 0.5 : false;

  // ~15% of employees want a role or project change
  const wantsRoleChange = random() < 0.15;

  return {
    daysSinceLastReview,
    daysSinceStart,
    managerChangedDaysAgo,
    reviewAfterManagerChange,
    wantsRoleChange,
    totalReviewsHeld
  };
};

export const evaluatePerformanceReviewScenarios = (
  metrics: PerformanceReviewMetrics,
  config: PerformanceReviewConfig = DEFAULT_PR_CONFIG
): PerformanceReviewScenario[] => {
  const scenarios: PerformanceReviewScenario[] = [];
  const { enabledSignals, thresholds } = config;

  // No review in 12 months
  if (enabledSignals.NO_REVIEW_12M && metrics.daysSinceLastReview !== null && metrics.daysSinceLastReview > thresholds.noReview12mDays) {
    scenarios.push({
      type: "NO_REVIEW_12M",
      severity: metrics.daysSinceLastReview > thresholds.noReview12mHighDays ? "high" : "medium",
      message: "No performance review in over 12 months",
      metricValue: metrics.daysSinceLastReview,
      unit: "days"
    });
  }

  // New manager, no review since then
  if (enabledSignals.NEW_MANAGER_NO_REVIEW && metrics.managerChangedDaysAgo !== null && !metrics.reviewAfterManagerChange) {
    scenarios.push({
      type: "NEW_MANAGER_NO_REVIEW",
      severity: "high",
      message: "Manager changed with no review since",
      metricValue: metrics.managerChangedDaysAgo,
      unit: "days ago"
    });
  }

  // Long tenure and never had a review
  if (enabledSignals.LONG_TENURE_NO_REVIEW && metrics.daysSinceStart > thresholds.longTenureDays && metrics.totalReviewsHeld === 0) {
    scenarios.push({
      type: "LONG_TENURE_NO_REVIEW",
      severity: "high",
      message: `Over ${Math.round(thresholds.longTenureDays / 30)} months tenure with no review`,
      metricValue: metrics.daysSinceStart,
      unit: "days"
    });
  }

  // Wants a role or project change
  if (enabledSignals.ROLE_CHANGE_REQUESTED && metrics.wantsRoleChange) {
    scenarios.push({
      type: "ROLE_CHANGE_REQUESTED",
      severity: "high",
      message: "Employee wants to change role or project",
      metricValue: 0,
      unit: ""
    });
  }

  // In probation review window, no review yet
  if (
    enabledSignals.FIRST_REVIEW_UPCOMING &&
    metrics.daysSinceStart >= thresholds.probationMinDays &&
    metrics.daysSinceStart <= thresholds.probationMaxDays &&
    metrics.totalReviewsHeld === 0
  ) {
    scenarios.push({
      type: "FIRST_REVIEW_UPCOMING",
      severity: "medium",
      message: "Probation review window — no review held yet",
      metricValue: metrics.daysSinceStart,
      unit: "days since start"
    });
  }

  return scenarios;
};

export const simulatePerformanceReviewRows = (
  employees: Employee[],
  dayKey: string = getSimulationDayKey(),
  maxEmployees: number = 15,
  config: PerformanceReviewConfig = DEFAULT_PR_CONFIG
): PerformanceReviewEmployeeRow[] => {
  const pickEmployees = (all: Employee[]) => {
    if (all.length <= maxEmployees) return all;
    const random = mulberry32(hashString(`perf-review:${dayKey}`));
    const shuffled = [...all];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, maxEmployees);
  };

  const employeesWithId = pickEmployees(employees).filter(
    (employee): employee is Employee & { id: string } =>
      typeof employee.id === "string" && employee.id.length > 0
  );

  const rows: PerformanceReviewEmployeeRow[] = [];

  for (const employee of employeesWithId) {
    const metrics = simulatePerformanceReviewMetrics(employee.id, dayKey);
    const scenarios = evaluatePerformanceReviewScenarios(metrics, config);

    if (scenarios.length === 0) continue;

    const topSeverity = scenarios.reduce((top, scenario) => {
      return severityRank[scenario.severity] > severityRank[top] ? scenario.severity : top;
    }, "medium" as PerformanceReviewSeverity);

    rows.push({ employee, metrics, scenarios, topSeverity });
  }

  return rows;
};

export const sortSignalsByPriority = (signals: PerformanceReviewScenario[]): PerformanceReviewScenario[] => {
  return [...signals].sort((a, b) => {
    if (severityRank[a.severity] !== severityRank[b.severity]) {
      return severityRank[b.severity] - severityRank[a.severity];
    }
    return signalPriority[a.type] - signalPriority[b.type];
  });
};

export const formatPrimarySignal = (signal: PerformanceReviewScenario): string => {
  switch (signal.type) {
    case "NO_REVIEW_12M":
      return `No review in ${signal.metricValue} days (>${365} days)`;
    case "NEW_MANAGER_NO_REVIEW":
      return `New manager ${signal.metricValue} days ago — no review held`;
    case "LONG_TENURE_NO_REVIEW":
      return `${signal.metricValue} days since start — never reviewed`;
    case "ROLE_CHANGE_REQUESTED":
      return "Wants to change role or project";
    case "FIRST_REVIEW_UPCOMING":
      return `Probation review due (${signal.metricValue} days since start)`;
    default:
      return signal.message;
  }
};

export const buildPerformanceReviewLaunchRequest = (employeeId: string, workflowId: string) => ({
  employeeId,
  workflowId,
  draft: false,
  automationData: {},
  resources: []
});

export type PerformanceReviewAggregateStats = {
  totalEmployees: number;
  totalAtRisk: number;
  highSeverityCount: number;
  overdueCount: number;
  neverReviewedCount: number;
};

export const calculateAggregateStats = (
  employees: Employee[],
  rows: PerformanceReviewEmployeeRow[]
): PerformanceReviewAggregateStats => {
  const highSeverityCount = rows.filter(r => r.topSeverity === "high").length;
  const overdueCount = rows.filter(r => r.scenarios.some(s => s.type === "NO_REVIEW_12M")).length;
  const neverReviewedCount = rows.filter(r => r.metrics.totalReviewsHeld === 0 && r.metrics.daysSinceStart > 180).length;

  return {
    totalEmployees: employees.length,
    totalAtRisk: rows.length,
    highSeverityCount,
    overdueCount,
    neverReviewedCount
  };
};
